"use client";
import { useState } from "react";
import { MenuItem } from "@/lib/strapi/types";

interface HamburgerMenuProps {
  menuItems: MenuItem[];
}

type MobileMenuItem = MenuItem & {
  _key: string;
  children?: MobileMenuItem[];
};

const normalizeMenuKey = (value: unknown, fallback: string): string => {
  // Strapi component ids are numeric, while parentId is stored as string in CMS.
  // Normalize both sides to string so parent-child matching is reliable.
  // This also keeps generated fallback keys (e.g. item-0) compatible.
  if (value === null || typeof value === "undefined") return fallback;
  const normalized = String(value).trim();
  return normalized || fallback;
};

export default function HamburgerMenu({ menuItems }: HamburgerMenuProps) {
  const [open, setOpen] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

  const getLinkProps = (item: MenuItem) => {
    const props: Record<string, string> = {
      href: item.link || "#",
    };

    if (item.openInNewTab || (item.link && (item.link.startsWith("http://") || item.link.startsWith("https://")))) {
      props.target = "_blank";
      props.rel = "noopener noreferrer";
    }

    return props;
  };

  const buildMenuTree = (items: MenuItem[]): MobileMenuItem[] => {
    const itemMap = new Map<string, MobileMenuItem>();
    const rootItems: MobileMenuItem[] = [];

    items.forEach((item, index) => {
      const key = normalizeMenuKey(item.id, `item-${index}`);
      itemMap.set(key, { ...item, _key: key, children: [] });
    });

    items.forEach((item, index) => {
      const itemKey = normalizeMenuKey(item.id, `item-${index}`);
      const currentItem = itemMap.get(itemKey);
      if (!currentItem) return;

      if (item.parentId) {
        const parent = itemMap.get(normalizeMenuKey(item.parentId, ""));
        if (parent) {
          parent.children = parent.children || [];
          parent.children.push(currentItem);
        } else {
          rootItems.push(currentItem);
        }
      } else {
        rootItems.push(currentItem);
      }
    });

    const sortRecursively = (itemsToSort: MobileMenuItem[]) => {
      itemsToSort.sort((a, b) => (a.order || 0) - (b.order || 0));
      itemsToSort.forEach((item) => {
        if (item.children && item.children.length > 0) {
          sortRecursively(item.children);
        }
      });
    };

    sortRecursively(rootItems);
    return rootItems;
  };

  const toggleExpanded = (key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const closeMenu = () => {
    setOpen(false);
    setExpandedKeys(new Set());
  };

  const renderMobileMenuItems = (items: MobileMenuItem[], depth = 0) => {
    return items.map((item) => {
      const hasChildren = Boolean(item.children && item.children.length > 0);
      const isExpanded = expandedKeys.has(item._key);
      const paddingLeft = `${16 + depth * 14}px`;

      if (hasChildren) {
        return (
          <li key={item._key}>
            <button
              type="button"
              className="w-full py-3 pr-4 rounded-xl text-lg font-semibold bg-white/10 hover:bg-white/20 backdrop-blur-lg border border-white/20 shadow flex items-center justify-between"
              style={{ paddingLeft }}
              onClick={() => toggleExpanded(item._key)}
              aria-expanded={isExpanded}
              aria-haspopup="menu"
            >
              <span>
                {item.icon && <i className={`${item.icon} mr-2`} />}
                {item.label}
              </span>
              <i className={`fas ${isExpanded ? "fa-chevron-up" : "fa-chevron-down"} text-xs`} />
            </button>

            {isExpanded && item.children && item.children.length > 0 && (
              <ul className="mt-2 flex flex-col gap-2">{renderMobileMenuItems(item.children, depth + 1)}</ul>
            )}
          </li>
        );
      }

      return (
        <li key={item._key}>
          <a
            {...getLinkProps(item)}
            className="block py-3 pr-4 rounded-xl text-lg font-semibold bg-white/10 hover:bg-white/20 backdrop-blur-lg border border-white/20 shadow"
            style={{ paddingLeft }}
            onClick={closeMenu}
          >
            {item.icon && <i className={`${item.icon} mr-2`} />}
            {item.label}
          </a>
        </li>
      );
    });
  };

  const menuTree = buildMenuTree(menuItems);

  return (
    <div className="md:hidden relative">
      <button
        aria-label="Open menu"
        className="fixed top-4 right-4 z-50 p-3 rounded-full bg-white/10 backdrop-blur-lg border border-white/20 shadow-lg"
        onClick={() => setOpen((v) => !v)}
      >
        <i className="fas fa-bars text-2xl" />
      </button>
      {open && (
        <div className="fixed top-0 right-0 w-full h-full bg-black/40 z-40" onClick={closeMenu} />
      )}
      <div
        className={`fixed top-0 right-0 w-4/5 max-w-xs h-full z-50 transition-transform duration-300 ${open ? "translate-x-0" : "translate-x-full"}`}
      >
        <nav
          className="h-full flex flex-col bg-white/10 backdrop-blur-lg rounded-l-2xl shadow-2xl border-l border-white/20"
          aria-label="Mobile menu"
        >
          <button
            aria-label="Close menu"
            className="self-end m-4 p-2 rounded-full bg-white/20"
            onClick={closeMenu}
          >
            <i className="fas fa-times text-xl" />
          </button>
          <ul className="flex-1 flex flex-col gap-4 px-8">
            {renderMobileMenuItems(menuTree)}
          </ul>
        </nav>
      </div>
    </div>
  );
}
